// O que a leitura do despacho devolve vira o cadastro preenchido (fase C, 000386). Portado de
// `src/routes/_authenticated/pericias.nova.tsx` (`montarDados` e as normalizações) e da tela de conferência de
// lá (`ConfirmacaoExtracao.tsx`: o apelido sugerido e as observações), no commit f5c1923. PURO, com prova
// própria (`tests/perito-despacho.mjs`).
//
// O que muda de lá: aqui o resultado não abre uma segunda tela de conferência, e sim o MESMO cadastro de seis
// passos, já preenchido — um caminho só de cadastro, em vez de dois que repetiriam os mesmos campos. Por isso
// esta função também diz o que a leitura ACHOU e o que FALTA, que é o que a conferência de lá mostrava pintando
// os campos vazios.
import { aplicarMascaraCnj, decodificarCnj, extrairNumeroCnj } from "./cnj";
import type { Parte } from "./tipos";
import { TIPOS_PERICIA, TRIBUNAIS } from "./tribunais";

/** O que a Edge Function `ler-despacho` devolve. Nulo é "o despacho não traz". */
export interface ExtracaoDoDespacho {
  numeroProcesso: string | null;
  tribunal: string | null;
  vara: string | null;
  segmento: string | null;
  tipoPericia: string | null;
  juiz: string | null;
  valorCausa: string | null;
  prazoAceitacao: string | null;
  dataNomeacao: string | null;
  dataEntregaLaudo: string | null;
  partes: Array<{ nome: string; tipo: string }>;
  resumo: string | null;
}

/** O cadastro preenchido, com o que a leitura achou e o que faltou dito à parte. */
export interface CadastroInicial {
  numero: string;
  apelido: string;
  tribunal: string;
  vara: string;
  tipo: string;
  dataNomeacao: string;
  dataEntrega: string;
  prazoAceitacao: string;
  proximaAcao: string;
  observacoes: string;
  partes: Parte[];
  achou: string[];
  faltou: string[];
}

const TIPOS_DE_PARTE = ["autor", "reu", "advogado", "terceiro"] as const;

/**
 * O tipo da parte. Lá "Ré" (a ré) e "Reclamada" (o réu da Justiça do Trabalho) caíam em "terceiro": a regra
 * procurava "réu" e "rép" e não a forma feminina, e só "reclamant" do lado do autor. Aqui as duas são réu.
 */
export function normalizarTipoParte(v: string): Parte["tipo"] {
  const s = v.toLowerCase().trim();
  if (s === "ré" || s === "re" || s.includes("reu") || s.includes("réu") || s.includes("requerid") || s.includes("reclamad")) return "reu";
  if (s.includes("advog")) return "advogado";
  if (s.includes("autor") || s.includes("requerent") || s.includes("reclamant")) return "autor";
  return (TIPOS_DE_PARTE as readonly string[]).includes(s) ? (s as Parte["tipo"]) : "terceiro";
}

/** A sigla num formato só: "TRT02", "TRT-2" e "trt 2" viram "TRT2". */
function chaveSigla(v: string): string {
  return v.toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/^([A-Z]+)0+(\d)/, "$1$2");
}

/** A sigla do tribunal da LISTA da casa, ou vazio: tribunal que não está na lista não entra no cadastro. */
export function normalizarTribunal(v?: string | null): string {
  if (!v) return "";
  const s = chaveSigla(v);
  if (!s) return "";
  const exato = TRIBUNAIS.find((t) => chaveSigla(t.sigla) === s);
  if (exato) return exato.sigla;
  // "TRT2SP" ou "TRIBUNALTJSP" contêm a sigla inteira; a mais longa ganha, para "TRF3" não virar "TRF".
  const contido = TRIBUNAIS.filter((t) => s.includes(chaveSigla(t.sigla))).sort((a, b) => chaveSigla(b.sigla).length - chaveSigla(a.sigla).length)[0];
  return contido?.sigla ?? "";
}

const semAcento = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");

/**
 * Só a vara que existe na lista do tribunal; senão, vazio (o aluno escolhe). Lá a regra pegava a PRIMEIRA vara
 * contida no texto, ou que o contivesse, e errava em silêncio: "21ª Vara do Trabalho de São Paulo" contém "1ª
 * Vara do Trabalho" (a lista vai até a 12ª), e "Vara do Trabalho", sozinha, está dentro das doze. Uma vara errada
 * preenchida parece certa a quem confere; vazia, ela é escolhida. Aqui:
 *   1. o nome igual;
 *   2. senão, a vara cujo nome está no texto SEM um algarismo colado antes (o "1ª" dentro de "21ª" não conta), e
 *      a mais longa, se houver mais de uma ("Vara de Família" e "Vara de Família e Sucessões");
 *   3. senão, a vara que contém o texto, só se for UMA: "Vara" sozinha está em todas e não decide nada.
 */
export function normalizarVara(tribunalSigla: string, v?: string | null): string {
  if (!v || !tribunalSigla) return "";
  const varas = TRIBUNAIS.find((t) => t.sigla === tribunalSigla)?.varas ?? [];
  const alvo = semAcento(v);
  if (!alvo) return "";
  const exata = varas.find((x) => semAcento(x) === alvo);
  if (exata) return exata;
  // O "ª" sai na normalização ("1ª Vara Cível" vira "1varacivel"): o algarismo antes só desqualifica quando o
  // nome da LISTA começa com algarismo, que é o caso do ordinal ("1ª Vara do Trabalho" dentro de "21ª…").
  const noTexto = varas.filter((x) => {
    const k = semAcento(x);
    const ordinal = /^[0-9]/.test(k);
    for (let i = alvo.indexOf(k); i >= 0; i = alvo.indexOf(k, i + 1)) if (!ordinal || i === 0 || !/[0-9]/.test(alvo[i - 1])) return true;
    return false;
  });
  if (noTexto.length) return noTexto.sort((a, b) => semAcento(b).length - semAcento(a).length)[0];
  const contem = varas.filter((x) => semAcento(x).includes(alvo));
  return contem.length === 1 ? contem[0] : "";
}

export function normalizarTipoPericia(v?: string | null): string {
  if (!v) return "";
  const s = v.toLowerCase();
  return TIPOS_PERICIA.find((t) => t.toLowerCase() === s) ?? TIPOS_PERICIA.find((t) => s.includes(t.toLowerCase()) || t.toLowerCase().includes(s)) ?? "";
}

/** Só a data AAAA-MM-DD de verdade passa; o resto fica vazio para o aluno preencher. */
export function dataIso(v?: string | null): string {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return "";
  const [a, m, d] = v.split("-").map(Number);
  const dt = new Date(Date.UTC(a, m - 1, d));
  return dt.getUTCFullYear() === a && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? v : "";
}

/** Verdadeiro quando o texto colado é SÓ o número do processo: aí o próprio CNJ preenche, sem a IA. */
export function ehSoNumeroCnj(texto: string): boolean {
  if (!extrairNumeroCnj(texto)) return false;
  const resto = texto
    .replace(/\d{7}[-.\s]?\d{2}[.\s]?\d{4}[.\s]?\d[.\s]?\d{2}[.\s]?\d{4}/, "")
    .replace(/\bprocesso\b|\bautos\b|\bn[ºo°.]*|:/gi, "")
    .trim();
  return resto.length === 0;
}

/** O teto das observações no banco (`perito_pericias_observacoes_check`, 000385). */
export const LIMITE_OBSERVACOES = 10000;
const ROTULO_DO_TEXTO = "--- Texto original ---\n";
const TEXTO_CORTADO = "\n[…] O texto passava do limite das observações e foi cortado aqui.";

/**
 * As observações com o texto colado no fim. Lá o texto ia inteiro, e um despacho de mais de 10.000 caracteres
 * faria o banco recusar a perícia — e a gravação recusada na fila desfaz a perícia inteira na tela. Aqui ele é
 * cortado para caber, e o corte é dito no próprio texto.
 */
function comTextoOriginal(cabeca: string, texto: string): string {
  const antes = cabeca ? `${cabeca}\n\n${ROTULO_DO_TEXTO}` : ROTULO_DO_TEXTO;
  const cabe = LIMITE_OBSERVACOES - antes.length;
  if (cabe <= TEXTO_CORTADO.length) return cabeca;
  return antes + (texto.length <= cabe ? texto : texto.slice(0, cabe - TEXTO_CORTADO.length) + TEXTO_CORTADO);
}

/** O apelido sugerido, do primeiro autor e do primeiro réu ("Silva × Banco X"), como lá. */
export function sugerirApelido(partes: Parte[]): string {
  const curto = (nome?: string) => nome?.trim().split(/\s+/).slice(0, 2).join(" ") ?? "";
  return [curto(partes.find((p) => p.tipo === "autor")?.nome), curto(partes.find((p) => p.tipo === "reu")?.nome)].filter(Boolean).join(" × ").slice(0, 40);
}

/**
 * O cadastro a partir da leitura (ou só do número colado, quando `ia` é nulo). As observações juntam o resumo, o
 * juiz, o valor da causa, o segmento e o texto original, como a conferência de lá gravava: são dados do despacho
 * que o cadastro não tem campo para guardar.
 */
export function montarCadastro(ia: ExtracaoDoDespacho | null, texto?: string, novoId: () => string = () => crypto.randomUUID()): CadastroInicial {
  const numeroBruto = ia?.numeroProcesso ?? (texto ? extrairNumeroCnj(texto) : null);
  const numero = numeroBruto ? aplicarMascaraCnj(numeroBruto) : "";
  const decod = numero ? decodificarCnj(numero) : null;
  const tribunal = normalizarTribunal(ia?.tribunal) || decod?.tribunalSigla || "";
  const partes: Parte[] = (ia?.partes ?? []).filter((p) => p.nome.trim()).map((p) => ({ id: novoId(), nome: p.nome.trim(), tipo: normalizarTipoParte(p.tipo || "") }));
  const vara = normalizarVara(tribunal, ia?.vara);
  const tipo = normalizarTipoPericia(ia?.tipoPericia);
  const dataNomeacao = dataIso(ia?.dataNomeacao);
  const dataEntrega = dataIso(ia?.dataEntregaLaudo);
  const prazoAceitacao = dataIso(ia?.prazoAceitacao);
  const segmento = ia?.segmento || decod?.segmentoLabel || "";
  const textoOriginal = texto?.trim() && !ehSoNumeroCnj(texto) ? texto.trim() : "";
  const cabeca = [
    ia?.resumo ?? "",
    ia?.juiz ? `Juiz(a): ${ia.juiz}` : "",
    ia?.valorCausa ? `Valor da causa: ${ia.valorCausa}` : "",
    segmento ? `Segmento: ${segmento}` : "",
  ].filter(Boolean).join("\n").slice(0, LIMITE_OBSERVACOES);
  const observacoes = textoOriginal ? comTextoOriginal(cabeca, textoOriginal) : cabeca;

  const achou = [
    numero && "o número do processo",
    tribunal && "o tribunal",
    vara && "a vara",
    tipo && "o tipo de perícia",
    partes.length > 0 && (partes.length === 1 ? "1 parte" : `${partes.length} partes`),
    dataNomeacao && "a data da nomeação",
    dataEntrega && "a entrega do laudo",
    prazoAceitacao && "o prazo de aceitação",
  ].filter(Boolean) as string[];
  const faltou = [
    !numero && "o número do processo",
    !tribunal && "o tribunal",
    !vara && "a vara",
    !tipo && "o tipo de perícia",
    partes.length === 0 && "as partes",
  ].filter(Boolean) as string[];

  return {
    numero, apelido: sugerirApelido(partes), tribunal, vara, tipo, dataNomeacao, dataEntrega, prazoAceitacao,
    proximaAcao: prazoAceitacao ? "Aceitar o encargo" : "", observacoes, partes, achou, faltou,
  };
}

/** A leitura que não achou nada que importe (lá: vazio de número, tribunal, tipo e partes vai ao manual com aviso). */
export function leituraVazia(c: CadastroInicial): boolean {
  return !c.numero && !c.tribunal && !c.tipo && c.partes.length === 0;
}

/** "o número do processo, o tribunal e 2 partes". */
export function emLista(itens: string[]): string {
  return itens.length > 1 ? `${itens.slice(0, -1).join(", ")} e ${itens.at(-1)}` : (itens[0] ?? "");
}
